import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import { formatDate } from '../../format.js';
import { displayPhone } from '../../phone.js';
import { Alert } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { AccountHead, useAccount, allowed } from './AccountLayout.jsx';
import { useAuth } from '../../auth.jsx';

// Demandes de soumission: what buyers asked about my products (the
// "Contact the seller" form, also emailed). List: date, product, buyer,
// quantity, status (new / read / replied); opening one marks it read.
// "Reply by email" opens the mail app and marks it replied.
const STATUSES = ['new', 'read', 'replied'];

export default function RequestsPage() {
  const { user } = useAuth();
  const { t, lang, errorText } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();
  const { reload } = useAccount();
  usePageTitle('accountNav.requests');
  const [params, setParams] = useSearchParams();
  const status = STATUSES.includes(params.get('status')) ? params.get('status') : '';
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => api(`/account/requests${status ? `?status=${status}` : ''}`).then(setItems).catch((e) => setError(errorText(e))), [status, errorText]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!id) { setDetail(null); return; }
    api(`/account/requests/${id}`).then((d) => { setDetail(d); setItems((list) => list?.map((x) => (x.id === d.id ? { ...x, status: d.status } : x))); reload(); })
      .catch((e) => setError(errorText(e)));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function setStatus(s) {
    try {
      const d = await api(`/account/requests/${detail.id}/status`, { method: 'POST', body: { status: s } });
      setDetail(d); setItems((list) => list?.map((x) => (x.id === d.id ? { ...x, status: d.status } : x))); reload();
    } catch (e) { setError(errorText(e)); }
  }
  const filter = (s) => { const n = new URLSearchParams(params); if (s) n.set('status', s); else n.delete('status'); setParams(n, { replace: true }); };
  const subject = detail ? encodeURIComponent(`${t('requestsPage.replySubject')} ${detail.listing.title}`) : '';

  return (
    <div className="acc-page">
      <AccountHead place={id ? 'ws.rfq.detail' : 'ws.rfq.list'} title={t('accountNav.requests')} lead={t('requestsPage.lead')} />
      <div className="acc-seg" role="group" aria-label={t('requestsPage.filter')}>
        {['', ...STATUSES].map((s) => <button key={s || 'all'} type="button" aria-pressed={status === s} onClick={() => filter(s)}>{s ? t(`requestsPage.status.${s}`) : t('requestsPage.all')}</button>)}
      </div>
      <Alert>{error}</Alert>
      {!items && !error && <p className="muted">{t('common.loading')}</p>}
      {items && items.length === 0 && <div className="acc-empty"><p className="strong">{t('requestsPage.empty')}</p><p className="muted">{t('requestsPage.emptyHint')}</p></div>}
      {items && items.length > 0 && (
        <div className="acc-req-layout">
          <ul className="acc-req-list">
            {items.map((r) => (
              <li key={r.id}>
                <Link to={`/account/requests/${r.id}${status ? `?status=${status}` : ''}`} className={`${r.status === 'new' ? 'is-new' : ''}${String(r.id) === id ? ' is-active' : ''}`} aria-current={String(r.id) === id ? 'true' : undefined}>
                  {r.listing.photo ? <img className="acc-thumb" style={{ width: 48, height: 48 }} src={r.listing.photo} alt="" /> : <span className="acc-thumb acc-thumb-empty" style={{ width: 48, height: 48 }}><Icon name="inbox" size={20} /></span>}
                  <span>
                    <span className="acc-req-top"><strong>{r.listing.title}</strong><span className={`chip-status chip-${r.status}`}>{t(`requestsPage.status.${r.status}`)}</span></span>
                    <span className="acc-req-meta">{formatDate(r.createdAt, lang)} · {r.buyer.company || r.buyer.name}{r.quantity ? ` · ${t('requestsPage.qty', { q: r.quantity })}` : ''}</span>
                    <span className="acc-req-excerpt">{r.excerpt}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <section className="card acc-req-detail" aria-live="polite">
            {!detail ? <p className="muted">{t('requestsPage.pick')}</p> : (
              <>
                <div className="acc-section-title">
                  <h2 className="acc-card-title">{detail.listing.title}</h2>
                  <span className={`chip-status chip-${detail.status}`}>{t(`requestsPage.status.${detail.status}`)}</span>
                </div>
                <dl className="kv">
                  <dt>{t('requestsPage.date')}</dt><dd>{formatDate(detail.createdAt, lang)}</dd>
                  <dt>{t('requestsPage.buyer')}</dt><dd>{detail.buyer.name}{detail.buyer.company ? ` — ${detail.buyer.company}` : ''}</dd>
                  <dt>{t('contact.email')}</dt><dd><a href={`mailto:${detail.buyer.email}`}>{detail.buyer.email}</a></dd>
                  {detail.buyer.phone && <><dt>{t('contact.phone')}</dt><dd><a href={`tel:${detail.buyer.phone}`}>{displayPhone(detail.buyer.phone)}</a></dd></>}
                  {detail.quantity && <><dt>{t('requestsPage.quantity')}</dt><dd>{detail.quantity}</dd></>}
                </dl>
                <div className="acc-req-body">{detail.body}</div>
                <div className="editor-actions" style={{ justifyContent: 'flex-start', marginTop: 14 }}>
                  <a className="btn btn-gold btn-sm" href={`mailto:${detail.buyer.email}?subject=${subject}`} onClick={() => setStatus('replied')}><Icon name="mail" size={16} /> {t('requestsPage.reply')}</a>
                  {detail.status !== 'replied' && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setStatus('replied')}>{t('requestsPage.markReplied')}</button>}
                  {detail.status !== 'new' && <button type="button" className="btn btn-link btn-sm" onClick={() => setStatus('new')}>{t('requestsPage.markNew')}</button>}
                  {allowed(user, 'docs.create') && allowed(user, 'customers.viewAll|customers.viewAssigned') && <Link className="btn btn-primary btn-sm" to={`/account/workspace/documents/new?rfqId=${detail.id}&kind=quotation`}>📄 {t('requestsPage.replyDoc')}</Link>}
                  {detail.chatId && <Link className="btn btn-primary btn-sm" to={`/account/messages/${detail.chatId}`}><Icon name="chat" size={16} /> {t('requestsPage.openChat')}</Link>}
                  {detail.listing.id && <Link className="btn btn-link btn-sm" to={`/account/listings/${detail.listing.id}/edit`}>{t('requestsPage.openProduct')}</Link>}
                  <button type="button" className="btn btn-link btn-sm" onClick={() => navigate(`/account/requests${status ? `?status=${status}` : ''}`)}>{t('common.close')}</button>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
